/**
 * Dokumente als Chips: je Dokument Dateisymbol und Name, dahinter „+ Dokument“.
 *
 *   [📄 Schallgutachten v3 ↗ ×]  [📄 Anhang Messpunkte ↗ ×]  [+ Dokument]
 *
 * - Ein Klick auf den Namen öffnet einen kleinen Editor mit Name, URL und „Ausgestellt am“ (der
 *   Name oben, weil man auf ihn geklickt hat) – dort wird auch
 *   entfernt. Der Pfeil öffnet das Dokument, das × entfernt es direkt (erscheint beim Überfahren).
 * - „+ Dokument“ öffnet denselben Editor leer, nur das Datum des zuletzt hinzugefügten Dokuments
 *   steht als Vorschlag darin. Nur die URL ist Pflicht; ohne Namen steht der Dateiname aus der
 *   URL da (der Server speichert ihn so). Das Datum darf nicht in der Zukunft liegen und steht
 *   hinter dem Namen im Chip.
 * - Gemeldet wird jede abgeschlossene Änderung als ganze Liste (`onChange`). Wann gespeichert
 *   wird, entscheidet der Aufrufer: im Nachweis-Detail sofort, im Dialog erst mit dem Knopf.
 *
 * Bewusst ohne Fachbezug – die Komponente kennt nur Link, Name und Ausstellungsdatum.
 */
import React, { useState } from "react";
import { Box, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { SvgIconComponent } from "@mui/icons-material";
import Button from "./Button";
import InputField from "./InputField";
import InputFieldDate from "./InputFieldDate";
import { format } from "date-fns";
import { borderRadius, colors, fonts } from "../../../styles/theme";
import { formatDate } from "../../../utils/formatters";
import {
  dateiendung,
  dokumentAnzeigename,
  dokumentnameAusUrl,
} from "../../../utils/dokumente";

export interface DokumentEintrag {
  url: string;
  name: string;
  /** Ausgestellt am (ISO-Datum) */
  issued_on?: string | null;
}

interface DokumentListeProps {
  dokumente: DokumentEintrag[];
  /** Die ganze Liste nach jeder abgeschlossenen Änderung (Hinzufügen, Bearbeiten, Entfernen) */
  onChange: (dokumente: DokumentEintrag[]) => void;
  readOnly?: boolean;
  /** Überschrift über den Chips, z. B. „Dokumente“ */
  label?: string;
  required?: boolean;
  error?: string;
}

const SYMBOL_JE_ENDUNG: Record<string, SvgIconComponent> = {
  pdf: PictureAsPdfOutlinedIcon,
  doc: DescriptionOutlinedIcon,
  docx: DescriptionOutlinedIcon,
  odt: DescriptionOutlinedIcon,
  rtf: DescriptionOutlinedIcon,
  txt: DescriptionOutlinedIcon,
  xls: TableChartOutlinedIcon,
  xlsx: TableChartOutlinedIcon,
  ods: TableChartOutlinedIcon,
  csv: TableChartOutlinedIcon,
  png: ImageOutlinedIcon,
  jpg: ImageOutlinedIcon,
  jpeg: ImageOutlinedIcon,
  gif: ImageOutlinedIcon,
  tif: ImageOutlinedIcon,
  tiff: ImageOutlinedIcon,
  dwg: ArchitectureOutlinedIcon,
  dxf: ArchitectureOutlinedIcon,
  zip: FolderZipOutlinedIcon,
};

/** Ohne Endung ist es meist ein Ordner oder eine Seite im Portal – dann ein Link-Symbol */
const symbolFuer = (url: string): SvgIconComponent => {
  const endung = dateiendung(url);
  if (!endung) return LinkOutlinedIcon;
  return SYMBOL_JE_ENDUNG[endung] ?? InsertDriveFileOutlinedIcon;
};

const CHIP_HOEHE = 32;

const chipSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  height: CHIP_HOEHE,
  maxWidth: "100%",
  pl: 1.25,
  pr: 0.5,
  borderRadius: `${borderRadius.full}px`,
  border: `1px solid ${colors.border.light}`,
  backgroundColor: colors.background.paper,
  transition: "border-color 150ms ease, box-shadow 150ms ease",
  "&:hover": {
    borderColor: colors.brand.primary,
    boxShadow: `0 1px 4px ${colors.shadow.light}`,
  },
  // Das × zeigt sich erst beim Überfahren – die Zeile bleibt ruhig.
  "& .dokument-entfernen": { opacity: 0, transition: "opacity 150ms ease" },
  "&:hover .dokument-entfernen, &:focus-within .dokument-entfernen": {
    opacity: 1,
  },
} as const;

/** Offener Editor: an welchem Element, für welches Dokument (Index) bzw. neu */
interface Editor {
  anchor: HTMLElement;
  index: number | null;
  url: string;
  name: string;
  issuedOn: string;
  fehler?: string;
  issuedOnFehler?: string;
}

const DokumentListe: React.FC<DokumentListeProps> = ({
  dokumente,
  onChange,
  readOnly = false,
  label,
  required = false,
  error,
}) => {
  const [editor, setEditor] = useState<Editor | null>(null);

  const oeffneEditor = (anchor: HTMLElement, index: number | null): void => {
    const dokument = index === null ? null : dokumente[index];
    setEditor({
      anchor,
      index,
      url: dokument?.url ?? "",
      name: dokument?.name ?? "",
      // Ein weiteres Dokument kommt meist mit dem vorigen – dessen Datum als Vorschlag.
      issuedOn: (dokument ?? dokumente[dokumente.length - 1])?.issued_on ?? "",
    });
  };

  const entfernen = (index: number): void => {
    setEditor(null);
    onChange(dokumente.filter((_, i) => i !== index));
  };

  const uebernehmen = (): void => {
    if (!editor) return;
    const url = editor.url.trim();
    // ISO-Datum: der Textvergleich ist der Datumsvergleich.
    const zukunft = editor.issuedOn > format(new Date(), "yyyy-MM-dd");
    if (!url || zukunft) {
      setEditor({
        ...editor,
        fehler: url ? undefined : "Pflichtfeld",
        issuedOnFehler: zukunft
          ? "Darf nicht in der Zukunft liegen"
          : undefined,
      });
      return;
    }
    const neu = {
      url,
      name: editor.name.trim() || dokumentnameAusUrl(url),
      issued_on: editor.issuedOn || null,
    };
    onChange(
      editor.index === null
        ? [...dokumente, neu]
        : dokumente.map((dokument, i) => (i === editor.index ? neu : dokument)),
    );
    setEditor(null);
  };

  return (
    <Box>
      {label && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 0.75,
            color: error ? colors.status.error.main : colors.text.secondary,
          }}
        >
          {label}
          {required && " *"}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {dokumente.map((dokument, index) => {
          const Symbol = symbolFuer(dokument.url);
          const name = dokumentAnzeigename(dokument);
          const ausgestellt = dokument.issued_on
            ? formatDate(dokument.issued_on)
            : null;
          return (
            <Box key={`${index}-${dokument.url}`} sx={chipSx}>
              <Symbol sx={{ fontSize: 18, color: colors.text.secondary }} />
              <Tooltip title={dokument.url} arrow>
                {readOnly ? (
                  // Lesend führt der Name direkt zum Dokument.
                  <Typography
                    component="a"
                    href={dokument.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    noWrap
                    sx={{
                      color: colors.text.primary,
                      textDecoration: "none",
                      fontWeight: fonts.weights.medium,
                      pr: 0.75,
                    }}
                  >
                    {name}
                  </Typography>
                ) : (
                  <Typography
                    component="button"
                    type="button"
                    variant="body2"
                    noWrap
                    onClick={(event: React.MouseEvent<HTMLElement>) =>
                      oeffneEditor(event.currentTarget, index)
                    }
                    sx={{
                      border: "none",
                      background: "none",
                      p: 0,
                      fontFamily: "inherit",
                      cursor: "pointer",
                      color: colors.text.primary,
                      fontWeight: fonts.weights.medium,
                      maxWidth: 280,
                    }}
                  >
                    {name}
                  </Typography>
                )}
              </Tooltip>
              {ausgestellt && (
                <Tooltip title="Ausgestellt am" arrow>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: colors.text.secondary,
                      pr: readOnly ? 0.75 : 0,
                    }}
                  >
                    {ausgestellt}
                  </Typography>
                </Tooltip>
              )}
              {!readOnly && (
                <>
                  <Tooltip title="Öffnen" arrow>
                    <IconButton
                      size="small"
                      component="a"
                      href={dokument.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${name} öffnen`}
                      sx={{ p: 0.5 }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Entfernen" arrow>
                    <IconButton
                      size="small"
                      className="dokument-entfernen"
                      aria-label={`${name} entfernen`}
                      onClick={() => entfernen(index)}
                      sx={{ p: 0.5 }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          );
        })}

        {!readOnly && (
          <Box
            component="button"
            type="button"
            onClick={(event: React.MouseEvent<HTMLElement>) =>
              oeffneEditor(event.currentTarget, null)
            }
            sx={{
              ...chipSx,
              pr: 1.5,
              cursor: "pointer",
              fontFamily: "inherit",
              borderStyle: "dashed",
              borderColor: error
                ? colors.status.error.main
                : colors.border.medium,
              backgroundColor: "transparent",
              color: colors.text.secondary,
              "&:hover": {
                borderColor: colors.brand.primary,
                color: colors.brand.primary,
              },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: fonts.weights.medium }}
            >
              Dokument
            </Typography>
          </Box>
        )}

        {readOnly && dokumente.length === 0 && (
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, fontStyle: "italic" }}
          >
            Kein Dokument hinterlegt
          </Typography>
        )}
      </Box>
      {error && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.5, color: colors.status.error.main }}
        >
          {error}
        </Typography>
      )}

      <Popover
        open={!!editor}
        anchorEl={editor?.anchor ?? null}
        onClose={() => setEditor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 1, p: 2, width: 420 } } }}
      >
        {editor && (
          <Box
            component="form"
            onSubmit={(event: React.FormEvent) => {
              event.preventDefault();
              uebernehmen();
            }}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <InputField
              label="Name"
              value={editor.name}
              onChange={(name) => setEditor({ ...editor, name })}
              // Leer gelassen, gilt der Dateiname aus der URL.
              placeholder={
                editor.url.trim()
                  ? dokumentnameAusUrl(editor.url)
                  : "aus der URL"
              }
              autoFocus={editor.index !== null}
            />
            <InputField
              label="URL"
              value={editor.url}
              onChange={(url) =>
                setEditor({ ...editor, url, fehler: undefined })
              }
              placeholder="Link zum Dokument"
              required
              error={editor.fehler}
              autoFocus={editor.index === null}
            />
            <InputFieldDate
              label="Ausgestellt am"
              value={editor.issuedOn}
              onChange={(issuedOn) =>
                setEditor({
                  ...editor,
                  issuedOn: issuedOn ?? "",
                  issuedOnFehler: undefined,
                })
              }
              variant="floating"
              error={editor.issuedOnFehler}
              fullWidth
            />
            <Box
              sx={{
                display: "flex",
                justifyContent:
                  editor.index === null ? "flex-end" : "space-between",
                gap: 1,
              }}
            >
              {editor.index !== null && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    editor.index !== null && entfernen(editor.index)
                  }
                >
                  Entfernen
                </Button>
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="secondary" onClick={() => setEditor(null)}>
                  Abbrechen
                </Button>
                <Button variant="primary" type="submit">
                  {editor.index === null ? "Hinzufügen" : "Übernehmen"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default DokumentListe;
